import React, { useState } from 'react';
import { FaCalculator, FaInfoCircle } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import ErrorMessage from '../components/Common/ErrorMessage';
import SuccessMessage from '../components/Common/SuccessMessage';

const Calculator = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    productCost: '',
    shippingCost: '',
    otherCosts: '',
    targetMargin: 30
  });
  const [results, setResults] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/calculator/calculate', {
        costs: {
          productCost: parseFloat(formData.productCost),
          shippingToAmazon: parseFloat(formData.shippingCost),
          otherCosts: parseFloat(formData.otherCosts) || 0
        },
        targetMargin: parseFloat(formData.targetMargin)
      });

      setResults(response.data.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to calculate pricing');
    } finally {
      setLoading(false);
    }
  };

  const getPriceColor = (option) => {
    const colors = {
      'Conservative': 'from-blue-500 to-blue-600',
      'Balanced': 'from-green-500 to-green-600',
      'Aggressive': 'from-orange-500 to-orange-600',
      'Premium': 'from-purple-500 to-purple-600'
    };
    return colors[option.name] || 'from-gray-500 to-gray-600';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">AI Pricing Calculator</h1>
          <p className="text-gray-600 mt-1">Get AI-powered pricing recommendations for your products</p>
        </div>
        <div className="flex items-center px-4 py-2 bg-blue-50 rounded-lg">
          <FaCalculator className="text-blue-600 mr-2" />
          <span className="text-sm font-medium text-blue-900">
            {user?.subscription?.plan === 'pro' ? 'Unlimited' : 'Available'}
          </span>
        </div>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError('')} />}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input Form */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">Product Costs</h2>
          
          <form onSubmit={handleCalculate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product Cost ($)
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                name="productCost"
                value={formData.productCost}
                onChange={handleChange}
                step="0.01"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="15.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Shipping to Amazon ($)
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                name="shippingCost"
                value={formData.shippingCost}
                onChange={handleChange}
                step="0.01"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="2.50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Other Costs ($)
              </label>
              <input
                type="number"
                name="otherCosts"
                value={formData.otherCosts}
                onChange={handleChange}
                step="0.01"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="1.00"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Margin (%)
              </label>
              <input
                type="number"
                name="targetMargin"
                value={formData.targetMargin}
                onChange={handleChange}
                min="0"
                max="100"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Calculating...' : 'Calculate Pricing'}
            </button>
          </form>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-start">
              <FaInfoCircle className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">How it works:</p>
                <ul className="space-y-1 text-blue-800">
                  <li>• Enter your product costs</li>
                  <li>• AI calculates FBA fees automatically</li>
                  <li>• Get 4 pricing strategies</li>
                  <li>• Choose the best option for your market</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="space-y-4">
          {loading ? (
            <div className="bg-white rounded-lg shadow-md p-12">
              <LoadingSpinner size="lg" text="Calculating optimal pricing..." />
            </div>
          ) : results ? (
            <>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Cost Breakdown</h2>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Product Cost:</span>
                    <span className="font-medium">${results.costs?.productCost?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Shipping:</span>
                    <span className="font-medium">${results.costs?.shippingToAmazon?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">FBA Fee:</span>
                    <span className="font-medium">${results.costs?.fbaFee?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Referral Fee:</span>
                    <span className="font-medium">${results.costs?.referralFee?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-gray-200">
                    <span className="font-semibold text-gray-900">Total Costs:</span>
                    <span className="font-bold text-gray-900">${results.costs?.totalCosts?.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <h2 className="text-xl font-semibold text-gray-900">Pricing Options</h2>
              
              {results.options?.map((option, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200">
                  <div className={`h-2 bg-gradient-to-r ${getPriceColor(option)}`}></div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-gray-900">{option.name}</h3>
                      <span className="text-2xl font-bold text-gray-900">${option.price?.toFixed(2)}</span>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-gray-500">Profit</p>
                        <p className="text-lg font-semibold text-green-600">${option.profit?.toFixed(2)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Margin</p>
                        <p className="text-lg font-semibold text-blue-600">{option.margin?.toFixed(1)}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">ROI</p>
                        <p className="text-lg font-semibold text-purple-600">{option.roi?.toFixed(1)}%</p>
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 italic">{option.recommendation}</p>
                  </div>
                </div>
              ))}

              {results.aiRecommendation && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border-2 border-blue-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                    <FaInfoCircle className="text-blue-600 mr-2" />
                    AI Recommendation
                  </h3>
                  <p className="text-gray-700">{results.aiRecommendation}</p>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <FaCalculator className="text-6xl text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Ready to Calculate</h3>
              <p className="text-gray-600">Enter your product costs to get AI-powered pricing recommendations</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Calculator;
